import { DEFAULT_PRICE_DECIMAL_PLACES } from '@template/types';
import { Decimal } from './decimal';
/**
 * 숫자를 천 단위 구분자와 함께 포맷팅
 * @param number - 포맷팅할 숫자 (undefined나 null인 경우 0으로 처리)
 * @param locale - 로케일 (기본값: 'ko-KR')
 * @param options - FormatNumberOptions (Intl.NumberFormatOptions + roundingMode)
 * @param options.roundingMode - 반올림 방식 ('round' | 'floor' | 'ceil', 기본값: 'floor')
 * @returns 포맷팅된 문자열
 */
export function formatNumber(number, locale = 'ko-KR', options) {
    // undefined나 null인 경우 0으로 처리
    const validNumber = number ?? 0;
    const roundingMode = options?.roundingMode ?? 'floor';
    const decimalPlaces = options?.maximumFractionDigits ?? options?.minimumFractionDigits ?? 0;
    // roundingMode에 따라 버림/올림/반올림 처리
    let roundedNumber;
    if (roundingMode === 'floor') {
        // 버림 (기본값)
        roundedNumber = new Decimal(validNumber)
            .toDecimalPlaces(decimalPlaces, Decimal.ROUND_DOWN)
            .toNumber();
    }
    else if (roundingMode === 'ceil') {
        // 올림
        roundedNumber = new Decimal(validNumber)
            .toDecimalPlaces(decimalPlaces, Decimal.ROUND_UP)
            .toNumber();
    }
    else {
        // 반올림
        roundedNumber = new Decimal(validNumber)
            .toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_UP)
            .toNumber();
    }
    // roundingMode를 제거하고 Intl.NumberFormat에 전달
    const { roundingMode: _, ...intlOptions } = options ?? {};
    return new Intl.NumberFormat(locale, intlOptions).format(roundedNumber);
}
/**
 * 과학적 표기법에서 소수점 자리수를 추출
 * 부동소수점 오차를 방지하기 위해 decimal.js를 사용합니다.
 * @param value - 과학적 표기법 숫자 (1.0e-4 형식)
 * @returns 소수점 자리수
 * @example
 * getDecimalPlaces(1.0e-4) // 4
 * getDecimalPlaces(1.0e-5) // 5
 * getDecimalPlaces(1.0E-6) // 6
 * getDecimalPlaces(0.0001) // 4
 * getDecimalPlaces(0.01) // 2
 */
export function getDecimalPlaces(value) {
    if (isNaN(value) || !isFinite(value) || value === 0) {
        return DEFAULT_PRICE_DECIMAL_PLACES;
    }
    // 정수인 경우
    if (Number.isInteger(value)) {
        return 0;
    }
    // 부동소수점 오차 방지를 위해 decimal.js를 사용
    const decimal = new Decimal(value);
    const str = decimal.toString().toLowerCase();
    // 과학적 표기법인 경우 (예: 1.0e-4 또는 1.0E-4)
    if (str.includes('e')) {
        const parts = str.split('e');
        const exponent = parseInt(parts[1], 10);
        // e-4 형태면 소수점 4자리
        if (exponent < 0) {
            return Math.abs(exponent);
        }
    }
    // 일반 소수 형식인 경우 (예: 0.01)
    if (str.includes('.')) {
        return str.split('.')[1].length;
    }
    return 0;
}
//# sourceMappingURL=number.js.map